import type { ComponentProps, ReactNode } from "react";
import { cx } from "./cx.js";
import { AlertIcon, InfoIcon } from "./icons.jsx";

/** Page header: eyebrow (optional), title, description, right-aligned actions. */
export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: ReactNode; title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="dt-page-header">
      <div className="dt-page-header__text">
        {eyebrow && <div className="dt-page-header__eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p className="dt-page-header__desc">{description}</p>}
      </div>
      {actions && <div className="dt-page-header__actions">{actions}</div>}
    </header>
  );
}

// `title` is ours, not the HTML attribute: without the Omit the two intersect to
// `string & ReactNode` and a card heading can only ever be a plain string.
export function Card({ className, title, description, actions, children, padded = true, ...props }: Omit<ComponentProps<"section">, "title"> & { title?: ReactNode; description?: ReactNode; actions?: ReactNode; padded?: boolean }) {
  return (
    <section className={cx("dt-card", !padded && "dt-card--flush", className)} {...props}>
      {(title || actions) && (
        <header className="dt-card__header">
          <div>
            {title && <h2 className="dt-card__title">{title}</h2>}
            {description && <p className="dt-card__desc">{description}</p>}
          </div>
          {actions && <div className="dt-card__actions">{actions}</div>}
        </header>
      )}
      <div className="dt-card__body">{children}</div>
    </section>
  );
}

export function Stack({ gap = 4, className, ...props }: ComponentProps<"div"> & { gap?: 1 | 2 | 3 | 4 | 5 | 6 | 8 }) {
  return <div className={cx("dt-stack", `dt-gap-${gap}`, className)} {...props} />;
}

export function Inline({ gap = 2, align = "center", className, ...props }: ComponentProps<"div"> & { gap?: 1 | 2 | 3 | 4 | 6; align?: "start" | "center" | "end" | "baseline" }) {
  return <div className={cx("dt-inline", `dt-gap-${gap}`, `dt-align-${align}`, className)} {...props} />;
}

/** Table with the console's density and hover treatment. Wrap rows in <thead>/<tbody> as usual. */
export function Table({ className, ...props }: ComponentProps<"table">) {
  return (
    <div className="dt-table-wrap">
      <table className={cx("dt-table", className)} {...props} />
    </div>
  );
}

export function KeyValue({ items }: { items: Array<{ key: ReactNode; value: ReactNode }> }) {
  return (
    <dl className="dt-kv">
      {items.map((it, i) => (
        <div key={i} className="dt-kv__row">
          <dt>{it.key}</dt>
          <dd>{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Callout({ tone = "info", title, children, className }: { tone?: "info" | "danger" | "warning" | "success" | undefined; title?: ReactNode; children?: ReactNode; className?: string | undefined }) {
  return (
    <div className={cx("dt-callout", `dt-callout--${tone}`, className)} role={tone === "danger" ? "alert" : undefined}>
      <span className="dt-callout__icon">{tone === "info" || tone === "success" ? <InfoIcon /> : <AlertIcon />}</span>
      <div className="dt-callout__body">
        {title && <p className="dt-callout__title">{title}</p>}
        {children}
      </div>
    </div>
  );
}

/** Inline form error line (for action results). */
export function FormError({ message }: { message?: string | null | undefined }) {
  if (!message) return null;
  return (
    <Callout tone="danger" className="dt-form-error">
      {message}
    </Callout>
  );
}

/** Centered single-column page for auth and onboarding flows. `back` is pinned to the top-left corner. */
export function CenteredPage({ children, width = 400, back }: { children: ReactNode; width?: number; back?: ReactNode }) {
  return (
    <main className={cx("dt-centered", back != null && "dt-centered--back")}>
      {back != null && <div className="dt-centered__back">{back}</div>}
      <div className="dt-centered__inner" style={{ maxWidth: width }}>
        {children}
      </div>
    </main>
  );
}

/** Avatar: the image when there is one, otherwise the first letter of the name. Square for workspaces, round for people. Decorative — name the owner next to it. */
export function Avatar({ name, src, size = 22, round = false }: { name: string; src?: string | null | undefined; size?: number; round?: boolean }) {
  return (
    <span className="dt-avatar" aria-hidden="true" style={{ width: size, height: size, borderRadius: round ? "50%" : Math.round(size * 0.27), fontSize: Math.max(12, Math.round(size * 0.42)) }}>
      {src ? <img src={src} alt="" /> : name.slice(0, 1)}
    </span>
  );
}

export function Brand({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  return (
    <span className={cx("dt-brand", `dt-brand--${size}`)}>
      <span className="dt-brand__mark" aria-hidden="true" />
      <span className="dt-brand__name">Diotia</span>
    </span>
  );
}
